import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, Shield } from "lucide-react";
import { fetchSettings, updateAlertConfig } from "@/lib/api";
import { toast } from "sonner";

export default function SettingsPage() {
  const [config, setConfig] = useState({ mention_spike_threshold: 20, rising_query_threshold: 100 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const settingsData = await fetchSettings();
      if (settingsData.alert_config) {
        setConfig(settingsData.alert_config);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      await updateAlertConfig(config);
      toast.success("Settings saved");
    } catch (e) { toast.error("Failed to save"); }
  };

  if (loading) {
    return <div className="space-y-4" data-testid="settings-loading">
      {[...Array(2)].map((_, i) => <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-none" />)}
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <Card className="glass-card border-gray-200 rounded-none" data-testid="alert-config-card">
        <CardHeader className="pb-2 border-b border-gray-200">
          <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Alert Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="font-display text-xs uppercase tracking-wider text-gray-600">Social Spike Threshold (per hour)</Label>
              <Input
                type="number"
                value={config.mention_spike_threshold}
                onChange={(e) => setConfig(c => ({ ...c, mention_spike_threshold: parseInt(e.target.value) || 0 }))}
                className="rounded-none bg-white border-gray-300 font-mono-data h-9"
                data-testid="settings-mention-threshold"
              />
              <p className="text-[10px] font-mono-data text-gray-400">Alert when a category gets this many mentions in 1 hour</p>
            </div>
            <div className="space-y-2">
              <Label className="font-display text-xs uppercase tracking-wider text-gray-600">Rising Query Threshold (%)</Label>
              <Input
                type="number"
                value={config.rising_query_threshold}
                onChange={(e) => setConfig(c => ({ ...c, rising_query_threshold: parseInt(e.target.value) || 0 }))}
                className="rounded-none bg-white border-gray-300 font-mono-data h-9"
                data-testid="settings-rising-threshold"
              />
              <p className="text-[10px] font-mono-data text-gray-400">Alert when a Google Trends rising query exceeds this %</p>
            </div>
          </div>
          <Button onClick={handleSave} className="mt-4 rounded-none bg-[#FFEE2F] text-black hover:bg-[#E6E100] font-display text-xs font-bold tracking-widest uppercase h-9 px-5" data-testid="settings-save-btn">
            SAVE SETTINGS
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
